import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { z } from 'zod';
import useAuth from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { resetPassword } = useAuth();

  const [email, setEmail] = useState(location.state?.email || '');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const emailSchema = z.string().min(1, 'Email is required').email('Invalid email address');
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setIsLoading(false);
      toast({
        title: 'Invalid email',
        description: parsed.error.errors[0].message,
        variant: 'destructive',
      });
      return;
    }

    const { error } = await resetPassword(email);
    setIsLoading(false);

    if (error) {
      const errorMessage = error?.message || String(error);
      console.error('Failed to send reset email:', errorMessage);

      toast({
        title: 'Failed to send reset email',
        description: 'Unable to send password reset email. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    setEmailSent(true);
    toast({
      title: 'Reset email sent',
      description: `We sent a password reset link to ${email}`,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-warm flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        <Card className="shadow-warm border-border/50">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl">Reset Password</CardTitle>
            <CardDescription>
              {emailSent
                ? 'Check your email for a password reset link'
                : "Enter your email address and we'll send you a link to reset your password"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!emailSent ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="border-border/50"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 shadow-soft"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    'Sending...'
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Reset Link
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg text-sm">
                  <p className="text-foreground">
                    We've sent a password reset link to <strong>{email}</strong>
                  </p>
                  <p className="text-muted-foreground mt-2">
                    Click the link in the email to set a new password. The link will expire in 1 hour.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/auth')}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center">
          <Button variant="ghost" onClick={() => navigate('/auth')} className="text-muted-foreground">
            ← Back to login
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
